import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import { TripPODResponse, TripStop, Note } from "../../../types/api";
import TripDateTimeline from "./TripDatesCard";
import { useTranslation } from 'react-i18next';
import { RelatedLoadsCards } from "./RelatedLoadsCards";
import { CheckRounded } from "@mui/icons-material";
import { timeline } from "../style";
import { useTripDocument } from "../../../context/TripDocumentContext";
import { PODPayloadFechtMetaData } from "../docPayloads";
import { useTripContext } from "../../../context/tripsContext";
import { useDaysDifference } from "../../../utils/hooks/useDaysDifference";

import loadIcon from "../../../assets/images/icons/loadCount.svg";
import hook from "../../../assets/images/icons/hook.svg";
import unhook from "../../../assets/images/icons/unhook.svg";
import pickup from "../../../assets/images/icons/pickUp.svg";
import dropoff from "../../../assets/images/icons/dropOff.svg";
import delivery from "../../../assets/images/icons/delivery.svg";
//TEST RILASCIO
interface TripTimelineProps {
  tripStops: TripStop[];
  notes?: Note[] | null;
}

const activityIcons: Record<string, JSX.Element> = {
  DELIVERY: <img src={delivery} alt="load icon" />,
  LOAD: <img src={loadIcon} alt="load icon" />,
  HOOK: <img src={hook} alt="hook icon" />,
  UNHOOK: <img src={unhook} alt="unhook icon" />,
  PICKUP: <img src={pickup} alt="pickup icon" />,
  DROPOFF: <img src={dropoff} alt="dropoff icon" />,
};

const activityPriorities: Record<string, number> = {
  LOAD: 1,
  HOOK: 2,
  PICKUP: 3,
  DELIVERY: 1,
  UNHOOK: 2,
  DROPOFF: 3,
};

const TripTimeline: React.FC<TripTimelineProps> = ({ tripStops, notes }) => {
  const { t } = useTranslation();
  const { tripDetails } = useTripContext();
  const { fetchLatestPODDoc } = useTripDocument();
  const [filteredDocuments, setFilteredDocuments] = useState<TripPODResponse[]>([]);
  const [isInitialFetchComplete, setIsInitialFetchComplete] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [searchTrigger, setSearchTrigger] = useState<number>(0);
  const [lastSearchedTrip, setLastSearchedTrip] = useState<string | null>(null);
  const hasTriggeredInitialSearch = useRef<string | null>(null);
  const fetchLatestPODDocRef = useRef(fetchLatestPODDoc);
  
  // Stato per il dialog delle note
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedStopNotes, setSelectedStopNotes] = useState<{
    invPartyName: string;
    notaOrdine: string | null;
    notaDisposizione: string | null;
  } | null>(null);

  // Aggiorna il ref quando fetchLatestPODDoc cambia
  useEffect(() => {
    fetchLatestPODDocRef.current = fetchLatestPODDoc;
  }, [fetchLatestPODDoc]);

  // Hook per calcolare la differenza in giorni
  const { daysDifference, isValid: isDaysValid, error: daysError } = useDaysDifference(tripDetails?.tripCreationDate);

  console.log('[DEBUG] TripTimeline - Days calculation:', {
    tripCreationDate: tripDetails?.tripCreationDate,
    daysDifference,
    isDaysValid,
    daysError
  });

  // Logica per raggruppare stops e notes per TMSLocation
  const groupedDataByTMSLocation = React.useMemo(() => {
    if (!tripStops || !notes) {
      return {};
    }

    const grouped: {
      [tmsLocation: string]: {
        stop: TripStop;
        notaOrdine: string | null; // Nota ordine da notes
        notaDisposizione: string | null; // Nota disposizione da relatedActivities
        relatedActivities: any[];
      };
    } = {};

    // Raggruppa gli stops per TMSLocation
    tripStops.forEach(stop => {
      // Estrai la nota disposizione da relatedActivities
      let notaDisposizione = null;
      if (stop.relatedActivities && stop.relatedActivities.length > 0) {
        // Prendi la prima attività che ha activityRemark
        const activityWithRemark = stop.relatedActivities.find(activity => activity.activityRemark);
        if (activityWithRemark) {
          notaDisposizione = activityWithRemark.activityRemark;
        }
      }

      grouped[stop.tmsLocation] = {
        stop,
        notaOrdine: null,
        notaDisposizione,
        relatedActivities: stop.relatedActivities || []
      };
    });

    // Raggruppa le note ordine per TMSLocation
    notes.forEach(note => {
      if (grouped[note.tmsLocation] && grouped[note.tmsLocation].notaOrdine === null) {
        grouped[note.tmsLocation].notaOrdine = note.remark;
      }
    });

    return grouped;
  }, [tripStops, notes]);

  // Console.log per visualizzare i dati combinati raggruppati
  console.log('[DEBUG] TripTimeline - Grouped data by TMSLocation:', groupedDataByTMSLocation);

  // Debug effect to track state changes
  useEffect(() => {
    console.log("TripTimeline: isInitialFetchComplete changed to:", isInitialFetchComplete);
  }, [isInitialFetchComplete]);

  // Reset state when trip changes
  useEffect(() => {
    if (tripDetails?.tmsObjectNumber) {
      console.log("TripTimeline: Trip changed, resetting state for:", tripDetails.tmsObjectNumber);
      setFilteredDocuments([]);
      setIsInitialFetchComplete(false);
      setIsFetching(false);
      setLastSearchedTrip(null);
    }
  }, [tripDetails?.tmsObjectNumber]);

  // Effect per fare la ricerca solo una volta quando tutti i dati sono pronti
  useEffect(() => {
    const currentTripId = tripDetails?.tmsObjectNumber;
    
    // Se non abbiamo un trip ID, non fare nulla
    if (!currentTripId) return;
    
    // Se abbiamo già fatto la ricerca per questo trip, non fare nulla
    if (lastSearchedTrip === currentTripId) return;
    
    // Se non abbiamo tutti i dati necessari, non fare nulla
    if (!isDaysValid || daysDifference === undefined) return;
    
    // Se stiamo già facendo una ricerca, non fare nulla
    if (isFetching) return;
    
    // Solo la prima volta per questo trip
    if (hasTriggeredInitialSearch.current !== currentTripId) {
      setLastSearchedTrip(currentTripId);
      setSearchTrigger(prev => prev + 1);
      hasTriggeredInitialSearch.current = currentTripId;
    }
  }, [tripDetails?.tmsObjectNumber, isDaysValid, daysDifference, lastSearchedTrip, isFetching]);

  // Funzione dedicata per il fetch dei documenti
  const fetchDocuments = useCallback(async (tripId: string, daysDiff: number) => {
    console.log("TripTimeline: Starting fetch for tripId:", tripId);
    setIsFetching(true);
    let isCurrent = true;

    try {
      const dynamicPayload = {
        ...PODPayloadFechtMetaData,
        searchFilterDtoList: PODPayloadFechtMetaData.searchFilterDtoList.map(
          (filter) => ({
            ...filter,
            fields: [
              ...filter.fields.filter((field) => field.name !== "TESTO227_131" && field.name !== "Creation_Date_NrGiorni"),
              {
                operator: 1,
                valore2: null,
                multiple: tripId,
                name: "TESTO227_131",
                externalId: "COD_VIAGGIO",
                className: "FieldBaseForSearchStringDto",
                groupId: 2,
                fieldType: 2,
                additionalFieldType: 0,
                defaultOperator: 1,
                tableName: "DM_PROFILE",
                binderFieldId: 0,
                description: "OPE - Codice Viaggio Consegna",
                order: 23,
                dataSource: null,
                required: false,
                formula: "",
                locked: false,
                comboGruppiId: null,
                dependencyFields: [],
                associations: null,
                isAdditional: true,
                visible: true,
                predefinedProfileFormula: null,
                dataSourceDiscriminator: null,
                and: null,
              },
              {
                operator: 3,
                valore1: daysDiff,
                valore2: null,
                multiple: null,
                name: "Creation_Date_NrGiorni",
                description: "Data reg. indietro di:",
                className: "FieldBaseForSearchIntDto",
              },
            ],
          })
        ),
      };

      console.log('[DEBUG] TripTimeline - Dynamic payload:', {
        tripId,
        daysDiff,
        payload: dynamicPayload
      });

      const response = await fetchLatestPODDocRef.current(dynamicPayload);
      console.log("TripTimeline: Fetch response:", response);
      
      if (!isCurrent) return;

      if (!response || !Array.isArray(response)) {
        console.log("TripTimeline: No valid response, setting empty documents");
        setFilteredDocuments([]);
        setIsInitialFetchComplete(true);
        return;
      }

      const groupedDocuments = response.reduce<{
        [key: string]: TripPODResponse;
      }>((acc, doc) => {
        if (!doc.TESTO246_136 || !doc.TESTO227_131 || !doc.DOCNUMBER) return acc;

        if (doc.TESTO227_131 === tripId) {
          const groupKey = doc.TESTO246_136;
          if (!acc[groupKey] || (acc[groupKey].DOCNUMBER || 0) < doc.DOCNUMBER) {
            acc[groupKey] = doc;
          }
        }
        return acc;
      }, {});

      console.log("TripTimeline: Setting filtered documents:", Object.values(groupedDocuments));
      setFilteredDocuments(Object.values(groupedDocuments));
      setIsInitialFetchComplete(true);
    } catch (err: any) {
      console.error("TripTimeline: Error fetching documents:", err);
      if (isCurrent) {
        setFilteredDocuments([]);
        setIsInitialFetchComplete(true);
      }
    } finally {
      if (isCurrent) {
        console.log("TripTimeline: Fetch completed, setting isFetching to false");
        setIsFetching(false);
      }
    }
  }, []); // Rimuovo le dipendenze per evitare il loop infinito

  // Effect dedicato per la ricerca dei documenti - dipende solo da searchTrigger
  useEffect(() => {
    if (searchTrigger === 0) return;
    
    const currentTripId = tripDetails?.tmsObjectNumber;
    if (!currentTripId || !isDaysValid || daysDifference === undefined) return;
    
    console.log("TripTimeline: Executing search with trigger:", searchTrigger);
    fetchDocuments(currentTripId, daysDifference);
  }, [searchTrigger, tripDetails?.tmsObjectNumber, isDaysValid, daysDifference, fetchDocuments]);

  let deliveryIndex = 0; // Contatore per gli indici incrementali dei relatedLoads

  // Funzione per aggiornare i documenti dopo l'upload
  const handleDocumentsUpdate = useCallback(() => {
    console.log("TripTimeline: Documents updated, re-fetching documents");
    if (!tripDetails?.tmsObjectNumber || !isDaysValid || daysDifference === undefined) return;
    
    // Invece di chiamare fetchDocuments, incrementa solo il trigger
    setTimeout(() => {
      setSearchTrigger(prev => prev + 1);
    }, 1000);
  }, [tripDetails?.tmsObjectNumber, isDaysValid, daysDifference]);

  // Funzioni per gestire il dialog delle note
  const handleOpenNotesDialog = (group: any) => {
    setSelectedStopNotes({
      invPartyName: group.stop.invPartyName,
      notaOrdine: group.notaOrdine,
      notaDisposizione: group.notaDisposizione
    });
    setNotesDialogOpen(true);
  };

  const handleCloseNotesDialog = () => {
    setNotesDialogOpen(false);
    setSelectedStopNotes(null);
  };

  // Se non ci sono stops, mostra un messaggio
  if (!tripStops || tripStops.length === 0) {
    return (
      <Box sx={timeline.wrapper}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          {t("screens.trips.timeline.noStops") || "Nessuna tappa disponibile"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={timeline.wrapper}>
      <Stepper orientation="vertical" sx={timeline.stepper}>
        {Object.values(groupedDataByTMSLocation).map((group, globalIndex) => {
          const stop = group.stop;
          // Estrai i tipi di attività da relatedActivities
          const activityTypes = stop.relatedActivities?.map(activity => activity.activityType) || [];
          const isDelivery = activityTypes.includes("DELIVERY");
          const highestPriorityActivity = activityTypes.sort(
            (a, b) => activityPriorities[a] - activityPriorities[b]
          )[0];

          const icon = highestPriorityActivity ? (
            activityIcons[highestPriorityActivity] || <Box sx={timeline.box} />
          ) : (
            <Box sx={timeline.box} />
          );
          return (
            <Step key={stop.tmsLocation} active>
              <StepLabel icon={<Box sx={timeline.stepperLabel}>{icon}</Box>}>
                <Box sx={timeline.box1}>
                  <Box sx={timeline.box2}>
                    {globalIndex === 0 && (
                      <Box sx={timeline.box3}>
                        <Typography sx={timeline.etichetta}>
                          {t("screens.trips.timeline.from")}:
                        </Typography>
                        <Typography sx={timeline.invPartyName}>
                          {stop.invPartyName || "-"}
                        </Typography>
                      </Box>
                    )}
                    {globalIndex === Object.keys(groupedDataByTMSLocation).length - 1 && (
                      <Box sx={timeline.box3}>
                        <Typography sx={timeline.etichetta}>
                          {t("screens.trips.timeline.to")}:
                        </Typography>
                        <Typography sx={timeline.invPartyName}>
                          {stop.invPartyName || "-"}
                        </Typography>
                      </Box>
                    )}
                    {globalIndex !== 0 &&
                      globalIndex !== Object.keys(groupedDataByTMSLocation).length - 1 && (
                        <Typography sx={timeline.invPartyName}>
                          {stop.invPartyName || "-"}
                        </Typography>
                      )}
                    <Typography sx={timeline.address}>
                      {stop.address}
                    </Typography>
                  </Box>
                  {(activityTypes.includes("LOAD") || isDelivery) && (
                    <Typography
                      sx={{
                        ...timeline.mandatoryText,
                        color: stop.mandatory ? "error.main" : "success.main",
                      }}
                    >
                      {stop.mandatory && (
                        <Box sx={timeline.mandatoryBox}>
                          <Typography sx={timeline.mandatoryTitle}>
                            {t("screens.trips.timeline.mandatory")}
                          </Typography>
                          <CheckRounded
                            color="success"
                            sx={timeline.checkIcon}
                          />
                        </Box>
                      )}
                    </Typography>
                  )}
                </Box>
              </StepLabel>
              <StepContent sx={timeline.stepContent}>
                <Card elevation={0} sx={timeline.card}>
                  <CardContent sx={timeline.cardContent}>
                    {highestPriorityActivity && (
                      <Chip
                        label={highestPriorityActivity}
                        size="small"
                        sx={timeline.chips}
                      />
                    )}

                    <TripDateTimeline tripStop={stop} />

                    {/* Visualizza le note raggruppate */}
                    {(group.notaOrdine || group.notaDisposizione) && (() => {
                      // Logica per determinare il limite di caratteri
                      // Se ci sono entrambe le note: limite 150 caratteri
                      // Se una delle due note manca: limite 300 caratteri
                      const hasBothNotes = group.notaOrdine && group.notaDisposizione;
                      const charLimit = hasBothNotes ? 150 : 300;
                      
                      // Controlla se c'è almeno un troncamento
                      const notaDisposizioneTruncated = group.notaDisposizione && group.notaDisposizione.length > charLimit;
                      const notaOrdineTruncated = group.notaOrdine && group.notaOrdine.length > charLimit;
                      const hasAnyTruncation = notaDisposizioneTruncated || notaOrdineTruncated;
                      
                      return (
                        <Box 
                          sx={{ 
                            mt: 2, 
                            mb: 2,
                            pl: 2,
                            pb: 2,
                            pr: 2,
                            border: '1px solid #e0e0e0', 
                            borderRadius: 1, 
                            backgroundColor: 'white',
                            display: 'flex',
                            flexDirection: 'row',
                          }}
                        >

                          <Box sx={{ pt: 2}}>
                            {group.notaOrdine && (
                              <Typography variant="body2" sx={{ mb: group.notaDisposizione ? 1 : 0, pr: hasAnyTruncation ? 6 : 0 }}>
                                <strong style={{ color: '#d32f2f' }}>{t('screens.trips.notesCard.note1')}:</strong> {
                                  group.notaOrdine.length > charLimit 
                                    ? group.notaOrdine.substring(0, charLimit) + '...' 
                                    : group.notaOrdine
                                }
                              </Typography>
                            )}
                            {group.notaDisposizione && (
                              <Typography variant="body2" sx={{ pr: hasAnyTruncation ? 6 : 0 }}>
                                <strong style={{ color: '#d32f2f' }}>{group.notaOrdine ? t('screens.trips.notesCard.note2') : t('screens.trips.notesCard.note1')}:</strong> {
                                  group.notaDisposizione.length > charLimit 
                                    ? group.notaDisposizione.substring(0, charLimit) + '...' 
                                    : group.notaDisposizione
                                }
                              </Typography>
                            )}
                          </Box>
                          {hasAnyTruncation && (
                            <Box sx={{ alignSelf: 'flex-start', justifySelf: 'flex-start' }}>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: 'primary.main', 
                                  fontSize: '12px',
                                  textDecoration: 'underline',
                                  fontWeight: 'bold',
                                  whiteSpace: 'nowrap',
                                  top: '8px',
                                  right: '8px',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    opacity: 0.8
                                  }
                                }}
                                onClick={() => handleOpenNotesDialog(group)}
                              >
                                {t('screens.trips.showMore')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })()}


                    {isDelivery && (
                      <>
                        {(() => {
                          console.log("TripTimeline: isInitialFetchComplete:", isInitialFetchComplete);
                          return !isInitialFetchComplete ? (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "100%",
                                height: "150px",
                              }}
                            >
                              <CircularProgress />
                            </div>
                          ) : (
                            stop.relatedLoads.map((load, loadIndex) => {
                              const currentIndex = deliveryIndex;
                              deliveryIndex++;

                              return (
                                tripStops &&
                                load &&
                                load.podSectionRequired &&
                                filteredDocuments && (
                                  <RelatedLoadsCards
                                    key={`${stop.tmsLocation}-${loadIndex}`}
                                    index={currentIndex}
                                    relatedLoads={[load]}
                                    filteredDocuments={filteredDocuments}
                                    tmsLocationRelatedLoad={load.tmsLocation}
                                    tmsLocation={stop.tmsLocation}
                                    onDocumentsUpdate={handleDocumentsUpdate}
                                  />
                                )
                              );
                            })
                          );
                        })()}
                      </>
                    )}
                  </CardContent>
                </Card>
              </StepContent>
            </Step>
          );
        })}
      </Stepper>

      {/* Dialog per le note del singolo stop */}
      <Dialog
        open={notesDialogOpen}
        onClose={handleCloseNotesDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '8px 12px',
          backgroundColor: '#d0d0d0',
          borderRadius: '0px'
        }}>
          <Typography sx={{ 
            fontSize: "18px", 
            fontWeight: "bold", 
            color: 'black',
            textTransform: 'uppercase'
          }}>
            {selectedStopNotes?.invPartyName}
          </Typography>
          <IconButton onClick={handleCloseNotesDialog} size="small">
            <CloseRounded />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ padding: '16px 24px' }}>
          {selectedStopNotes && (
            <Box>
              {selectedStopNotes.notaOrdine && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ 
                    fontSize: "14px", 
                    fontWeight: "bold", 
                    color: 'primary.main',
                    mb: 1
                  }}>
                    {t('screens.trips.notesCard.note1')}: <span style={{ fontWeight: 'normal', color: 'black' }}>{selectedStopNotes.notaOrdine}</span>
                  </Typography>
                </Box>
              )}
              {selectedStopNotes.notaOrdine && selectedStopNotes.notaDisposizione && (
                <Box sx={{ borderBottom: '1px solid #e0e0e0', mb: 2 }} />
              )}
              {selectedStopNotes.notaDisposizione && (
                <Box>
                  <Typography sx={{ 
                    fontSize: "14px", 
                    fontWeight: "bold", 
                    color: 'primary.main',
                    mb: 1
                  }}>
                    {selectedStopNotes.notaOrdine ? t('screens.trips.notesCard.note2') : t('screens.trips.notesCard.note1')}: <span style={{ fontWeight: 'normal', color: 'black' }}>{selectedStopNotes.notaDisposizione}</span>
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
export default TripTimeline;
